import Cookies from 'js-cookie';

const COOKIE_KEY = 'acquisition_data';
const COOKIE_DAYS = 30;

// Last-resort fallback when both cookie and localStorage are blocked.
// Persists across client-side navigation within the same tab session.
let _inMemoryData = null;

// Memoized — these never change within a session
const TIMEZONE = typeof Intl !== 'undefined'
  ? Intl.DateTimeFormat().resolvedOptions().timeZone
  : null;

let _parsedUA = null;

function parseUserAgent() {
  if (_parsedUA) return _parsedUA;
  if (typeof navigator === 'undefined') return (_parsedUA = {});

  const uaData = navigator.userAgentData;
  if (uaData) {
    const brand = uaData.brands?.find(
      (b) => !b.brand.includes('Not') && !b.brand.includes('Chromium')
    );
    return (_parsedUA = {
      browser:     brand?.brand || null,
      browser_ver: brand?.version || null,
      os:          uaData.platform || null,
      os_ver:      null,
      is_mobile:   uaData.mobile,
    });
  }

  const ua = navigator.userAgent;
  let browser = null, browser_ver = null, os = null, os_ver = null, m;

  if ((m = ua.match(/Firefox\/([\d.]+)/)))              { browser = 'Firefox'; browser_ver = m[1]; }
  else if ((m = ua.match(/Edg\/([\d.]+)/)))             { browser = 'Edge';    browser_ver = m[1]; }
  else if ((m = ua.match(/Chrome\/([\d.]+)/)))          { browser = 'Chrome';  browser_ver = m[1]; }
  else if ((m = ua.match(/Version\/([\d.]+).*Safari/))) { browser = 'Safari';  browser_ver = m[1]; }

  if ((m = ua.match(/iPhone OS ([\d_]+)/)))      { os = 'iOS';     os_ver = m[1].replace(/_/g, '.'); }
  else if ((m = ua.match(/iPad.*OS ([\d_]+)/)))  { os = 'iPadOS';  os_ver = m[1].replace(/_/g, '.'); }
  else if ((m = ua.match(/Android ([\d.]+)/)))   { os = 'Android'; os_ver = m[1]; }
  else if ((m = ua.match(/Windows NT ([\d.]+)/))){ os = 'Windows'; os_ver = m[1]; }
  else if ((m = ua.match(/Mac OS X ([\d_]+)/)))  { os = 'macOS';   os_ver = m[1].replace(/_/g, '.'); }
  else if (/Linux/.test(ua))                     { os = 'Linux'; }

  return (_parsedUA = {
    browser, browser_ver, os, os_ver,
    is_mobile: /Mobi|Android|iPhone|iPad/.test(ua),
  });
}

const PLACEHOLDER_RE = /^(__.*__|{{.*}})$/;

// Parse the actual browser URL — always accurate, even when router.query lags behind
// (e.g. TikTok in-app browser redirect chain settles the URL before router.query updates)
function parseWindowQuery() {
  if (typeof window === 'undefined') return {};
  const out = {};
  new URLSearchParams(window.location.search).forEach((val, key) => { out[key] = val; });
  return out;
}

// Resolve query params: window.location takes precedence over router.query for UTM fields,
// then null out any unsubstituted ad-platform macros (__AID_NAME__, {{campaign.name}}, etc.)
function resolveQuery(routerQuery) {
  const urlQuery = parseWindowQuery();
  const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

  // Merge: URL-parsed values win over router.query (router may be stale during redirects)
  const merged = { ...routerQuery };
  for (const key of Object.keys(urlQuery)) {
    const routerVal = routerQuery[key];
    const urlVal    = urlQuery[key];
    // Always prefer the URL value when router has a placeholder but URL has a real value,
    // or when the URL simply has a value that router doesn't
    const routerIsPlaceholder = routerVal && PLACEHOLDER_RE.test(routerVal);
    if (urlVal && (!routerVal || routerIsPlaceholder)) merged[key] = urlVal;
  }

  // Final safety net: null out any remaining unsubstituted macros
  UTM_KEYS.forEach((k) => {
    if (merged[k] && PLACEHOLDER_RE.test(merged[k])) merged[k] = null;
  });

  return merged;
}

export function captureAcquisition(rawQuery) {
  try {
    const query = resolveQuery(rawQuery);
    const hasUtms = query.utm_source || query.utm_medium || query.utm_campaign
      || query.utm_content || query.utm_term;
    const hasClickIds = query.gclid || query.fbclid || query.ttclid || query.ScCid;
    const hasAdParams = query.matchtype || query.network || query.extensionid
      || query.placement || query.target;

    const existing = getAcquisitionData();

    // Nothing new — context already captured, no incoming campaign params
    if (!hasUtms && !hasClickIds && !hasAdParams && existing.context) return;

    const conn = typeof navigator !== 'undefined' ? navigator.connection : null;
    const ua = parseUserAgent();

    const isFirstVisit = !existing.context;

    const data = {
      ...existing,
      device_type: typeof window !== 'undefined'
        ? (ua.is_mobile ? 'mobile' : 'desktop')
        : existing.device_type,
      // Capture referrer + landing page on first visit even without UTMs (e.g. organic Google)
      ...(isFirstVisit && {
        referrer:     typeof document !== 'undefined' ? document.referrer || null : null,
        landing_page: typeof window !== 'undefined' ? window.location.href : null,
      }),
      context: typeof window !== 'undefined' ? {
        screen_width:       window.screen.width,
        screen_height:      window.screen.height,
        viewport_width:     window.innerWidth,
        viewport_height:    window.innerHeight,
        device_pixel_ratio: window.devicePixelRatio,
        timezone:           TIMEZONE,
        language:           navigator.language,
        connection_type:    conn?.type || null,
        connection_speed:   conn?.effectiveType || null,
        touch_enabled:      navigator.maxTouchPoints > 0,
        browser:            ua.browser,
        browser_ver:        ua.browser_ver,
        os:                 ua.os,
        os_ver:             ua.os_ver,
      } : existing.context,
      // First touch — lock in the original source on the very first campaign visit
      ...((hasUtms || hasClickIds) && !existing.first_touch_utm_source && {
        first_touch_utm_source: query.utm_source || null,
        first_touch_data: {
          utm_source:   query.utm_source   || null,
          utm_medium:   query.utm_medium   || null,
          utm_campaign: query.utm_campaign || null,
          utm_content:  query.utm_content  || null,
          utm_term:     query.utm_term     || null,
          gclid:   query.gclid   || null,
          fbclid:  query.fbclid  || null,
          ttclid:  query.ttclid  || null,
          ScCid:   query.ScCid   || null,
          matchtype:   query.matchtype   || null,
          network:     query.network     || null,
          extensionid: query.extensionid || null,
          placement:   query.placement   || null,
          target:      query.target      || null,
          referrer:     typeof document !== 'undefined' ? document.referrer || null : null,
          landing_page: typeof window !== 'undefined' ? window.location.href : null,
        },
      }),
      // Last touch — always overwrite with the latest campaign data
      ...(hasUtms && {
        utm_source:   query.utm_source   || null,
        utm_medium:   query.utm_medium   || null,
        utm_campaign: query.utm_campaign || null,
        utm_content:  query.utm_content  || null,
        utm_term:     query.utm_term     || null,
        referrer:     typeof document !== 'undefined' ? document.referrer || null : null,
        landing_page: typeof window !== 'undefined' ? window.location.href : null,
      }),
      ...(hasClickIds && {
        gclid:   query.gclid   || null,
        fbclid:  query.fbclid  || null,
        ttclid:  query.ttclid  || null,
        ScCid:   query.ScCid   || null,
      }),
      ...(hasAdParams && {
        matchtype:   query.matchtype   || null,
        network:     query.network     || null,
        extensionid: query.extensionid || null,
        placement:   query.placement   || null,
        target:      query.target      || null,
      }),
    };

    saveData(data);
  } catch {
    // Never block the user flow over tracking
  }
}

// Safari ITP and in-app browsers (Snapchat, TikTok) silently block cookies.
// Write to cookie first, verify it stuck, fall back to localStorage if not.
function saveData(data) {
  const value = JSON.stringify(data);
  try {
    Cookies.set(COOKIE_KEY, value, { expires: COOKIE_DAYS });
    if (!Cookies.get(COOKIE_KEY)) {
      console.warn('acquisition_data: cookie blocked, falling back to localStorage', {
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      });
      localStorage.setItem(COOKIE_KEY, value);
    }
  } catch {
    try {
      localStorage.setItem(COOKIE_KEY, value);
    } catch {
      try {
        console.warn('acquisition_data: localStorage blocked, falling back to sessionStorage', {
          user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
          data
        });
        // sessionStorage survives full page reloads within the same tab
        sessionStorage.setItem(COOKIE_KEY, value);
      } catch {
        // All storage blocked — in-memory only (survives client-side navigation, not full reloads)
        _inMemoryData = data;
        console.error('acquisition_data: all storage blocked', {
          user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
          'in_memory': true,
          'data': data,
        });
      }
    }
  }
}

export function getAcquisitionData() {
  try {
    const raw = Cookies.get(COOKIE_KEY)
      || localStorage.getItem(COOKIE_KEY)
      || sessionStorage.getItem(COOKIE_KEY);
    if (raw) return JSON.parse(raw);
    if (_inMemoryData) return _inMemoryData;
    // Last resort: read directly from the current URL (works when params were forwarded)
    return parseWindowQuery();
  } catch {
    return _inMemoryData || parseWindowQuery();
  }
}
